//
// # Netlist Builder
//
// Builds a netlist from a schema schematic. Shared entry point for any flow
// that needs netlist data:
// - Layout conns population
// - Symmetry inference
// - Future: LVS, parasitic annotation
//

import { Result } from "../errors";
import { schema } from "GenAlphaSchema";
import { Context } from "CoreUtils";
import type { DepotGet } from "Depot";
import { Schematic } from "../schematic";
import { extractNetlist } from "./extractor";
import type { ComputedNetlist, NetlistWarning } from "./types";

// Result of building a netlist from a schema schematic
export interface NetlistBuildResult {
  modelSchematic: Schematic;
  netlist: ComputedNetlist;
  warnings: NetlistWarning[];
}

// Error type for netlist build failures
export interface NetlistBuildError {
  why: "model-build-failed" | "netlist-extraction-failed";
  error: unknown;
}

/**
 * Build a netlist from a schema schematic.
 *
 * Pipeline:
 *   schema schematic -> Schematic.fromSchema (resolves symbols via depot) -> extractNetlist()
 */
export async function buildNetlistFromSchematic(
  ctx: Context,
  schematic: schema.Schematic,
  depot: DepotGet,
  namespace: string
): Promise<Result<NetlistBuildResult, NetlistBuildError>> {
  // 1. Convert schema -> model, resolving symbols via depot.
  const modelSchematicResult = await Schematic.fromSchema(
    ctx,
    schematic,
    depot,
    namespace
  );
  if (modelSchematicResult.err) {
    return Result.Err({
      why: "model-build-failed",
      error: modelSchematicResult.val,
    });
  }
  const modelSchematic = modelSchematicResult.val;

  // 2. Extract netlist from the model schematic.
  const netlistResult = extractNetlist(modelSchematic, schematic.ident);
  if (netlistResult.err) {
    return Result.Err({
      why: "netlist-extraction-failed",
      error: netlistResult.val,
    });
  }

  return Result.Ok({
    modelSchematic,
    netlist: netlistResult.val.netlist,
    warnings: netlistResult.val.warnings,
  });
}
